import { useTranslation } from "react-i18next";
import { format } from "date-fns/format";
import { getDateFnsLocale } from "@tellescope/utils";
import { Progress, ToggleGroup, ToggleGroupItem } from "@tellescope/ui";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import type { WeightDisplayUnit } from "../../../utils/nutritionUnitConversions";

interface DeltaIndicatorProps {
  change: number;
  unit: string;
  decimals: number;
}

const DeltaIndicator = ({ change, unit, decimals }: DeltaIndicatorProps) => {
  const rounded = Number(change.toFixed(decimals));
  if (rounded === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" />
        {unit === "kcal" ? "0" : `0${unit}`}
      </span>
    );
  }
  const isUp = rounded > 0;
  const magnitude = Math.abs(rounded).toFixed(decimals);
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs ${isUp ? "text-emerald-600" : "text-red-600"}`}>
      {isUp ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {unit === "kcal" ? magnitude : `${magnitude}${unit}`}
    </span>
  );
};

interface MetricStatHeaderProps {
  date: string;
  value: number;
  prevValue?: number;
  unit: string;
  decimals: number;
  target?: number;
  isLatest?: boolean;
  isWeight?: boolean;
  weightUnit?: WeightDisplayUnit;
  onWeightUnitChange?: (unit: WeightDisplayUnit) => void;
  metricName: string;
}

export const MetricStatHeader = ({
  date,
  value,
  prevValue,
  unit,
  decimals,
  target,
  isLatest,
  isWeight,
  weightUnit,
  onWeightUnitChange,
  metricName,
}: MetricStatHeaderProps) => {
  const { t, i18n } = useTranslation("portal");
  const locale = getDateFnsLocale(i18n.language);

  const handleWeightUnitChange = (unitValue: string) => {
    if ((unitValue === "lb" || unitValue === "kg") && onWeightUnitChange) onWeightUnitChange(unitValue);
  };

  const targetPercentage = target ? Math.min(100, Math.round((value / target) * 100)) : undefined;

  return (
    <div className="space-y-3">
      <div className="flex justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">
            {format(new Date(date), "EEEE, MMMM d, yyyy", { locale })}
            {isLatest && (
              <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide">{t("Latest")}</span>
            )}
          </p>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span className="text-3xl font-bold tabular-nums">
              {metricName} {value.toFixed(decimals)}
            </span>
            <span className="text-sm text-muted-foreground">{unit}</span>
            {prevValue !== undefined && !isWeight && (
              <DeltaIndicator change={value - prevValue} unit={unit} decimals={decimals} />
            )}
          </div>
          {target !== undefined && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("{{metric}} target: {{target}} {{unit}}", {
                metric: metricName.toLowerCase(),
                target: target.toLocaleString(),
                unit,
              })}
            </p>
          )}
        </div>
        {isWeight && weightUnit && onWeightUnitChange && (
          <ToggleGroup type="single" value={weightUnit} onValueChange={handleWeightUnitChange}>
            <ToggleGroupItem value="lb">{t("lb")}</ToggleGroupItem>
            <ToggleGroupItem value="kg">{t("kg")}</ToggleGroupItem>
          </ToggleGroup>
        )}
      </div>
      {targetPercentage !== undefined && <Progress value={targetPercentage} />}
    </div>
  );
};
